import { useEffect, useState } from "react";
import { Button, Modal, Progress } from "antd";
import { LinkOutlined } from "@ant-design/icons";
import { DateUtils, DATE_FORMAT_MMDDYYYY } from "utils/dateutils";

/** The link API owns the first 70%; refetching the design package owns the rest. */
const LINK_PROGRESS = 70;

export type RelinkMilestoneSide = {
  name: string;
  actualDate: string | null;
  committedDate: string | null;
};

function milestoneWhen(side: RelinkMilestoneSide): string | null {
  if (side.actualDate) {
    return `Actual ${DateUtils.format(side.actualDate, DATE_FORMAT_MMDDYYYY)}`;
  }
  if (side.committedDate) {
    return `Committed ${DateUtils.format(side.committedDate, DATE_FORMAT_MMDDYYYY)}`;
  }
  return null;
}

function MilestoneSide(props: { label: string; side: RelinkMilestoneSide }) {
  const when = milestoneWhen(props.side);
  return (
    <div className="flex gap-3 px-3 py-2.5">
      <span className="w-9 shrink-0 text-xs font-normal text-ciq-black-a65">
        {props.label}
      </span>
      <div className="min-w-0">
        <div className="truncate text-xs font-semibold text-ciq-black-a85">
          {props.side.name}
        </div>
        {when ? (
          <div className="text-xs font-normal text-ciq-black-a65">{when}</div>
        ) : null}
      </div>
    </div>
  );
}

function nextPercent(current: number, ceiling: number): number {
  if (current >= ceiling) return ceiling;
  const step = Math.max(0.6, (ceiling - current) * 0.08);
  return Math.min(ceiling, current + step);
}

export default function RelinkMilestoneModal(props: {
  open: boolean;
  phase: "confirm" | "progress" | "error";
  /** "link" fills to 70%. "refresh" fills the remaining 30%. */
  work?: "link" | "refresh";
  bidLabel: string;
  from: RelinkMilestoneSide;
  to: RelinkMilestoneSide;
  errorMessage?: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const {
    open,
    phase,
    work = "link",
    bidLabel,
    from,
    to,
    errorMessage,
    onCancel,
    onConfirm
  } = props;
  const confirming = phase === "confirm";
  const failed = phase === "error";
  const [percent, setPercent] = useState(0);

  useEffect(() => {
    if (!open || confirming || failed) {
      if (!open || confirming) setPercent(0);
      return undefined;
    }
    const ceiling = work === "link" ? LINK_PROGRESS - 1 : 98;
    const floor = work === "link" ? 0 : LINK_PROGRESS;
    setPercent((current) => Math.max(current, floor));
    const timer = window.setInterval(() => {
      setPercent((current) => nextPercent(Math.max(current, floor), ceiling));
    }, 120);
    return () => window.clearInterval(timer);
  }, [open, confirming, failed, work]);

  return (
    <Modal
      open={open}
      centered
      width={440}
      destroyOnHidden
      title={
        <div className="flex items-center gap-3">
          {confirming ? (
            <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ciq-neutral-25 text-base text-ciq-black-a65">
              <LinkOutlined />
            </span>
          ) : (
            <span className="relative inline-block h-7 w-7 shrink-0">
              {/* The ring length is sized for a 100px view box. A small size
                  leaves the stroke off the circle, so draw it at 100 and scale. */}
              <span className="absolute left-0 top-0 origin-top-left scale-[0.28]">
                <Progress
                  className="!m-0"
                  type="circle"
                  percent={percent}
                  size={100}
                  strokeWidth={10}
                  showInfo={false}
                  status={failed ? "exception" : "normal"}
                  strokeColor={
                    failed ? "var(--ciq-red-700)" : "var(--ciq-primary-800)"
                  }
                />
              </span>
            </span>
          )}
          <span className="text-base font-semibold leading-6 text-ciq-black-a85">
            {confirming
              ? "Link to a different milestone?"
              : failed
                ? "Couldn't update the link"
                : `Linking to ${to.name}...`}
          </span>
        </div>
      }
      closable={confirming || failed}
      mask={{ closable: confirming || failed }}
      keyboard={confirming || failed}
      onCancel={() => {
        if (confirming || failed) onCancel();
      }}
      footer={
        confirming ? (
          <div className="flex justify-end gap-2">
            <Button data-testid="relink-cancel" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="primary"
              data-testid="relink-confirm"
              onClick={onConfirm}
            >
              Link to {to.name}
            </Button>
          </div>
        ) : failed ? (
          <div className="flex justify-end">
            <Button
              type="primary"
              data-testid="relink-error-close"
              onClick={onCancel}
            >
              Close
            </Button>
          </div>
        ) : null
      }
    >
      <div data-testid="relink-modal" data-phase={phase}>
        {confirming ? (
          <>
            <p className="m-0 text-sm font-normal leading-5 text-ciq-black-a65">
              {bidLabel} will be linked to {to.name}.
            </p>
            <div className="mt-4 overflow-hidden rounded-md border border-solid border-ciq-gray-200 bg-ciq-neutral-25">
              <div data-testid="relink-from">
                <MilestoneSide label="From" side={from} />
              </div>
              <div className="mx-3 border-0 border-t border-solid border-ciq-gray-200" />
              <div data-testid="relink-to">
                <MilestoneSide label="To" side={to} />
              </div>
            </div>
            <p className="mb-0 mt-4 text-xs font-normal leading-5 text-ciq-black-a45">
              Scope items and bid dates stay the same.
            </p>
          </>
        ) : failed ? (
          <p className="m-0 text-xs font-normal leading-5 text-ciq-black-a65">
            {errorMessage ||
              "The milestone link was not changed. Please try again."}
          </p>
        ) : (
          <p className="m-0 text-xs font-normal leading-5 text-ciq-black-a65">
            Updating {bidLabel}.{" "}
            <span className="block">
              This usually takes a few seconds — please keep this window open.
            </span>
          </p>
        )}
      </div>
    </Modal>
  );
}
